import { apiClient } from "../../lib/apiClient";
import { fileDownloader } from "../../lib/download";
import type {
  ApplyRequest,
  ApplyResult,
  Snapshot,
  SnapshotPlan,
  SnapshotSections,
} from "./types";

/** Base path of the three snapshot endpoints for one project. */
function base(projectId: string): string {
  return `/api/admin/projects/${encodeURIComponent(projectId)}/snapshot`;
}

/** Base path of the Bots page's endpoints, which the export card also reads. */
function botsBase(projectId: string): string {
  return `/api/admin/projects/${encodeURIComponent(projectId)}/bots`;
}

/**
 * The pair the export card prints beside each of the two bot-source rows —
 * "200 rows, 164 of them already spent here".
 */
export interface SourceCounts {
  /** Every row of the table, spent ones included: those travel too. */
  total: number;
  /** Rows this environment has already turned into a bot, or into a guild. */
  spent: number;
}

/**
 * The two numbers behind the `bot_roster` include row.
 *
 * Read off the Bots page's own list endpoint rather than a snapshot one: it
 * answers `{rows, total, summary}` and its summary counts the whole table, so
 * `limit=1` asks for the numbers without a page of rows nobody is looking at.
 * Member-readable, exactly like the phrase list this card already calls.
 *
 * The two fetchers live here rather than in `features/bots/api.ts` on purpose:
 * the shape this card needs is a pair of counts, not the Source tab's runway
 * strip, and the mapping from that strip's vocabulary onto "total / spent" is
 * this screen's business.
 */
export async function getRosterCounts(projectId: string): Promise<SourceCounts> {
  const r = await apiClient.get<{ summary: { total: number; used: number } }>(
    `${botsBase(projectId)}/roster?limit=1`,
  );
  return { total: r.data.summary.total, spent: r.data.summary.used };
}

/**
 * The same pair for `bot_guild_templates`.
 *
 * "Spent" is one column on both tables — `used_guild_id IS NOT NULL` here — but
 * the summary splits it in two, because a template whose guild still stands and
 * one whose guild has since been disbanded are different things to an operator.
 * They are the same thing to an export: both are rows this environment has
 * already used, and migration 0060 never clears that link on its own.
 */
export async function getGuildTemplateCounts(
  projectId: string,
): Promise<SourceCounts> {
  const r = await apiClient.get<{
    summary: { total: number; created: number; guild_disbanded: number };
  }>(`${botsBase(projectId)}/guild-templates?limit=1`);
  const s = r.data.summary;
  return { total: s.total, spent: s.created + s.guild_disbanded };
}

/**
 * POST /snapshot/plan — parse the file, diff it against current state and
 * return the plan. Writes nothing, so it is safe to call on every file drop.
 * Super admin only; a member gets 403.
 */
export async function planSnapshot(
  projectId: string,
  snapshot: Snapshot,
): Promise<SnapshotPlan> {
  const r = await apiClient.post<SnapshotPlan>(`${base(projectId)}/plan`, snapshot);
  return r.data;
}

/**
 * POST /snapshot/apply — recompute the plan server-side and commit the
 * selected sections in one transaction. `body.etag` comes from the plan; a
 * project that moved in between answers 409 `etag_mismatch`.
 */
export async function applySnapshot(
  projectId: string,
  body: ApplyRequest,
): Promise<ApplyResult> {
  const r = await apiClient.post<ApplyResult>(`${base(projectId)}/apply`, body);
  return r.data;
}

/**
 * GET /snapshot — download the file. Each deselected section is passed as
 * `<name>=0`, which omits that key from the envelope entirely; the default is
 * all five, so only the exclusions travel in the query string.
 *
 * Returns the filename the browser actually saved — the server's
 * Content-Disposition when it names one, else `fallbackFilename`. Pass the
 * value of `snapshotFilename()` there so a server that omits the header still
 * produces the name the export card promised.
 */
export async function downloadSnapshot(
  projectId: string,
  sections: SnapshotSections,
  fallbackFilename = "snapshot.json",
): Promise<string> {
  const params = new URLSearchParams();
  if (!sections.config) params.set("config", "0");
  if (!sections.bot_settings) params.set("bot_settings", "0");
  if (!sections.leaderboards) params.set("leaderboards", "0");
  if (!sections.phrases) params.set("phrases", "0");
  // The two bot-source params are named as the handler reads them —
  // `roster` and `guild_templates`, not the table names.
  if (!sections.roster) params.set("roster", "0");
  if (!sections.guild_templates) params.set("guild_templates", "0");
  const query = params.toString();
  const path = query ? `${base(projectId)}?${query}` : base(projectId);
  return fileDownloader.download(path, fallbackFilename);
}

/**
 * The name the exported file gets: `<slug>-<YYYY-MM-DD>.snapshot.json`, where
 * the slug is the project name lowercased with every run of non-alphanumerics
 * collapsed to a single `-` (§4.1). Kept in lockstep with the server, which
 * builds the real Content-Disposition name the same way — this copy exists so
 * the export card's footer can show the name before the download happens.
 *
 * The date is read in local time: it is the date on the human's own calendar
 * that they will look for in their downloads folder.
 */
export function snapshotFilename(
  projectName: string,
  date: Date = new Date(),
): string {
  // A name with no ASCII letters or digits at all — one written entirely in
  // Cyrillic, say — would collapse to a lone separator and give every such
  // project the same filename.
  const slug =
    projectName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") ||
    "project";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${slug}-${yyyy}-${mm}-${dd}.snapshot.json`;
}
